import cn from 'classnames'
import { type ReactNode, useMemo, useRef, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { type IModalContent, ModalDialog } from 'core-components/library/modalDialog'
import { Card } from 'partner360/components/card'
import { CockpitCardHeader } from 'partner360/features/financialCockpit/ui/components/cockpitCardHeader'
import { CockpitIconButton } from 'partner360/features/financialCockpit/ui/components/cockpitIconButton'
import {
  PLAN_SECTION_DIALOG_ATTRIBUTE,
  registerPlanSectionDeckSlot,
} from 'partner360/lib/planSectionDialog'
import { SectionLoading, useContentLoading } from 'shared/components/sectionLoading'

import { PLAN_SECTION_DIALOG_TRANSLATION_KEYS } from '../translations'

import { PlanSectionHeaderSlotContext } from './PlanSectionHeaderActions'

import styles from './PlanSectionDialog.module.scss'

export type PlanSectionEditMode = {
  isOn: boolean
  onToggle: () => void
}

export type PlanSectionDialogProps = {
  show: boolean
  /** Translation key of the section's name, the one its tab carries. */
  title: string
  /** Drawn in the title's place when the title is more than one key, e.g. a group and an item. */
  heading?: ReactNode
  /** What the section is planned for: the partner, its PID, the offering and the year. */
  description?: ReactNode
  /** The page's edit mode, switched from the dialog because the dialog covers the header's own
   *  switch. Left out where the user may not edit the plan. */
  editMode?: PlanSectionEditMode
  /** The section is saving: the dialog stays open and edit mode stays as it is until it ends. */
  isBusy?: boolean
  /** The section's data is being replaced, as on a year switch: the window's loading state covers
   *  it from that moment until the section has loaded again. */
  isReloading?: boolean
  onClose: () => void
  /** `lg` for a section that reads as one of the acknowledgment dialogs opened elsewhere, the
   *  size those take; the rest open over the whole screen. */
  size?: 'fullscreen' | 'lg'
  children: ReactNode
}

type PlanSectionCardProps = IModalContent &
  Omit<PlanSectionDialogProps, 'show' | 'onClose' | 'size'>

const PlanSectionCard = ({
  title,
  heading,
  description,
  editMode,
  isBusy = false,
  isReloading = false,
  children,
  closeModal,
}: PlanSectionCardProps) => {
  const t = useSafetyTranslation()
  const sectionRef = useRef<HTMLDivElement>(null)
  /* Each section loads its own way — spinners, overlays, parts drawn one by one. The window covers
     the first load with one loading state of its own, so every section opens alike. */
  const isLoading = useContentLoading(sectionRef, isReloading)
  /* The section may draw its own controls in the header, before the window's actions. */
  const [actionsSlot, setActionsSlot] = useState<HTMLSpanElement | null>(null)
  const headerSlot = useMemo(() => ({ slot: actionsSlot }), [actionsSlot])

  return (
    <>
      {/* The cockpit draws its plan deck here, over the scrim at the top of the screen. */}
      <div ref={registerPlanSectionDeckSlot} className={styles.deckSlot} />
      <Card
        as="div"
        expanded
        hoverable={false}
        className={styles.card}
        headerClassName={styles.cardHeader}
        bodyClassName={styles.cardBody}
        header={
          <div className={styles.heading}>
            <CockpitCardHeader
              title={heading ?? <Translation span text={title} />}
              description={description}
              actions={
                <>
                  <span ref={setActionsSlot} className={styles.headerSlot} />
                  {editMode && (
                    <CockpitIconButton
                      showLabel
                      icon="kbase kbase-edit"
                      label={t(PLAN_SECTION_DIALOG_TRANSLATION_KEYS.editMode)}
                      pressed={editMode.isOn}
                      disabled={isBusy}
                      onClick={editMode.onToggle}
                    />
                  )}
                  <CockpitIconButton
                    icon="kbase kbase-close"
                    label={t(PLAN_SECTION_DIALOG_TRANSLATION_KEYS.close)}
                    disabled={isBusy}
                    onClick={closeModal}
                  />
                </>
              }
            />
          </div>
        }
      >
        <div className={styles.sectionFrame}>
          <div
            ref={sectionRef}
            className={styles.section}
            {...{ [PLAN_SECTION_DIALOG_ATTRIBUTE]: '' }}
          >
            <PlanSectionHeaderSlotContext.Provider value={headerSlot}>
              {children}
            </PlanSectionHeaderSlotContext.Provider>
          </div>

          {/* Beside the section, not in it: the section is watched for its loaders. */}
          <SectionLoading show={isLoading} name={t(title)} className={styles.loading} />
        </div>
      </Card>
    </>
  )
}

/**
 * One plan section of the partner page over the cockpit, drawn as the manager cockpit's full-screen
 * attention card. It stops above the sections bar pinned to the bottom of the screen, so the bar
 * stays in reach and switches the section the dialog shows.
 */
export const PlanSectionDialog = ({
  show,
  onClose,
  size = 'fullscreen',
  ...cardProps
}: PlanSectionDialogProps) => (
  <ModalDialog
    show={show}
    size={size}
    isContainerRestricted={false}
    closeOnOutsideClick={false}
    classes={{
      overlay: styles.overlay,
      window: cn(styles.window, { [styles.windowLg]: size === 'lg' }),
      header: styles.modalHeader,
      body: styles.modalBody,
    }}
    content={PlanSectionCard}
    contentProps={cardProps}
    onClose={onClose}
  />
)

PlanSectionDialog.displayName = 'PlanSectionDialog'
